<template>
	<AssemblyLayout :assembly="assembly" :subtitle="trans('assemblies.elections.legal-ranks.title')">
		<template v-if="canCreate" #actions>
			<select v-model="legalRank">
				<option v-for="legalRankOption in legalRanks" :value="legalRankOption">{{ trans(`association.boards.legal_ranks.${legalRankOption}`) }}</option>
			</select>
			<PrimaryButton :color="Color.Green" @click.stop="store">
				<i class="fa-solid fa-plus mr-2"/>
				{{ trans('general.create') }}
			</PrimaryButton>
		</template>
		<Empty v-if="elections.length === 0"/>
		<CardGrid v-else>
			<Card v-for="election in elections" :key="`legal-rank-election-${election.id}`" :title="trans(`association.boards.legal_ranks.${election.legal_rank}`)">
				<template #badges>
					<Badge v-if="election.open" :color="Color.Green" active>{{ trans('general.open') }}</Badge>
					<Badge v-else :color="Color.Red">{{ trans('general.closed') }}</Badge>
				</template>
				<LabeledObject>
					<template #label>{{ trans('assemblies.seats') }}</template>
					<p>{{ election.limit }}</p>
				</LabeledObject>
				<template #footer>
					<Link :as="SecondaryButton" :color="Color.Blue" :href="route('association.assemblies.elections.legal-ranks.show', {assembly: assembly.id, election: election.id})">
						<i class="fa-solid fa-edit mr-2"/>
						{{ trans('general.edit') }}
					</Link>
				</template>
			</Card>
		</CardGrid>
	</AssemblyLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {ref, type Ref} from 'vue'
import {Link} from '@inertiajs/vue3'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/UI/Empty.vue'
import Card from '@/Components/UI/Cards/Card.vue'
import Badge from '@/Components/UI/Badges/Badge.vue'
import {LegalRank} from '@/Types/Enums/LegalRank'
import CardGrid from '@/Components/UI/Cards/CardGrid.vue'
import AssemblyLayout from '@/Layouts/AssemblyLayout.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {type Assembly} from '@/Types/Association/Assemblies/Assembly'
import {showInfo, showSuccess, showValidationErrors} from '@/Helpers/general'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import {type LegalRankElection} from '@/Types/Elections/Assemblies/LegalRankElection'

const props = defineProps<{
	canCreate:boolean,
	assembly:Assembly,
	selectedTab:string,
	legalRanks:Array<LegalRank>,
	elections:Array<LegalRankElection>,
}>()

const working:Ref<boolean> = ref(false)
const canCreate:Ref<boolean> = ref(props.canCreate)
const assembly:Ref<Assembly> = ref(props.assembly)
const legalRank:Ref<LegalRank> = ref(props.legalRanks[0])
const legalRanks:Ref<Array<LegalRank>> = ref(props.legalRanks)
const elections:Ref<Array<LegalRankElection>> = ref(props.elections)

function store():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.inserting'))
		axios.post(route('association.assemblies.elections.legal-ranks.store', assembly.value.id), {
			legal_rank: legalRank.value,
		}).then(({data}:{data:LegalRankElection}) => {
			showSuccess(trans('general.inserted'))
			canCreate.value = false
			elections.value.unshift(data)
			const index = legalRanks.value.findIndex((r:LegalRank) => r === legalRank.value)
			if(index > -1){
				legalRanks.value.splice(index, 1)
			}
			legalRank.value = legalRanks.value[0]
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>